// Amount display, shared by both apps — an amount has to read the same in the merchant dashboard and in the admin
// panel reviewing it. Mirrors the backend Asset model (io.bitcashier.common.Asset): known crypto codes are
// "coins", everything else (EUR, USD, GBP, JPY, CNH, …) is treated as fiat — the same default the backend applies.
// Coins keep their on-chain precision (grouped integer part, trailing zeros trimmed); fiat is rounded to its currency
// fraction digits (2, or 0 for zero-decimal currencies like JPY) with grouped thousands.
//
// The dashboard-only formatters (scientific-notation expansion, the numeric-amount predicate, file sizes) are NOT
// here — they live in dashboard-svelte/src/lib/core/formatters.js, so this module stays what both apps need.

// Crypto assets handled by the platform (kept in sync with Asset.nonIso4217Assets coins on the backend).
const COINS = new Set([
  "BTC",
  "ETH",
  "XRP",
  "LTC",
  "TRX",
  "USDT",
  "USDC",
  "BNB",
  "HEX",
  "ADA",
  "SOL",
  "AVAX",
  "DOGE",
  "BUSD",
  "DOT",
  "SHIB",
  "MATIC",
  "WBTC",
  "ATOM",
  "DAI",
  "TUSD",
]);

// Fiat currencies with no minor unit (rendered without decimals).
const ZERO_DECIMAL_FIAT = new Set(["JPY", "KRW", "CLP", "ISK", "VND", "HUF"]);

const isCoin = (asset) => !!asset && COINS.has(String(asset).toUpperCase());

const fiatFractionDigits = (asset) => (ZERO_DECIMAL_FIAT.has(String(asset).toUpperCase()) ? 0 : 2);

// Insert thousands separators into a run of integer digits without going through Number (preserves the full
// precision of large or many-decimal crypto amounts, which would lose digits via parseFloat).
const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// The amount formatted for display, without the asset symbol (the asset informs coin-vs-fiat precision only).
// Returns "-" for a missing amount and the raw string for anything non-numeric.
export const formatAmount = (amount, asset) => {
  if (amount === undefined || amount === null || amount === "") return "-";
  let s = String(amount).trim();
  const negative = s.startsWith("-");
  if (negative) s = s.slice(1);
  if (!/^\d*\.?\d*$/.test(s) || s === "" || s === ".") return String(amount);

  const sign = negative ? "-" : "";
  // Coins — and amounts with no asset (legacy rows), which we must not force into fiat 2-decimals — keep their given
  // precision. Only a known/assumed fiat asset is rounded to its currency fraction digits.
  if (!asset || isCoin(asset)) {
    const [intPart = "0", fracPart = ""] = s.split(".");
    const trimmedFrac = fracPart.replace(/0+$/, "");
    return sign + groupThousands(intPart || "0") + (trimmedFrac ? `.${trimmedFrac}` : "");
  }

  const decimals = fiatFractionDigits(asset);
  const fixed = Number(s).toFixed(decimals);
  const [intPart, fracPart = ""] = fixed.split(".");
  return sign + groupThousands(intPart) + (decimals > 0 ? `.${fracPart}` : "");
};

// The amount rendered with its coin/asset symbol (e.g. "0.5 ETH", "1,001,234.28 EUR"). Amount may arrive as a number
// or an already-stringified decimal depending on the endpoint. The asset is absent on some rows (e.g. records captured
// before it was tracked), so the bare amount is shown then; a missing amount renders as a dash.
export const formatAmountWithAsset = (amount, asset) => {
  if (amount === undefined || amount === null) return "-";
  const formatted = formatAmount(amount, asset);
  return asset ? `${formatted} ${asset}` : formatted;
};

// Normalizes a free-typed amount string before submission: strips spaces, accepts a comma as the decimal separator,
// and prefixes a leading "." with "0" (".5" -> "0.5"). Used by the filter forms' min/max amount inputs.
export const normalizeAmountInput = (value) => {
  const cleaned = value.replace(/ /g, "").replace(/,/, ".");
  return cleaned.startsWith(".") ? "0" + cleaned : cleaned;
};
